import { Button, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { PhoneOutlined } from "@material-ui/icons";
import { useRouter } from "next/router";
import React from "react";
import AppConfig from "../../../AppConfig";
import ContactFormModal from "../../modals/ContactFormModal";

const useStyles = makeStyles((theme) => ({
  megaContainer: {
    objectFit: "cover",
    position: "relative",
    width: "100%",
    padding: "0rem 0rem 1rem 0rem",
  },
  imageAndTextContainer: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  illustrationSizer: {
    objectFit: "cover",
    width: "100%",
    height: "100%",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      height: "90vh",
    },
  },
  overlayContainer: {
    position: "absolute",
    bottom: "1rem",
    width: "100%",
    display: "flex",
    zIndex: 999,
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "0rem 8rem 0rem 8rem",
    margin: "2rem 0rem 1rem 0rem",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      padding: "0rem 2rem 0rem 2rem",
      margin: "0rem 0rem 0rem 0rem",
    },
    [theme.breakpoints.only("sm")]: {
      padding: "0rem 4rem 0rem 4rem",
      margin: "1rem 0rem 1rem 0rem",
    },
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  heading: {
    fontSize: "2.5rem",
    fontWeight: 600,
    lineHeight: "3.5rem",
    color: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.25rem",
      lineHeight: "2rem",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.5rem",
      lineHeight: "2rem",
    },
  },
  subheading: {
    fontSize: "1.5rem",
    fontWeight: 400,
    lineHeight: "2rem",
    color: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.875rem",
      lineHeight: "1.5rem",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.875rem",
      lineHeight: "1.5rem",
    },
  },
  btnRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    gap: "1rem",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "row",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      gap: "1rem",
      margin: "1rem 0rem 1rem 0rem",
      width: "100%",
    },
  },
  primaryButton: {
    padding: "0.5rem 1rem",
    background: "linear-gradient(to right, #0FCA56 0%, #08A945 100%)",
    color: "#FFFFFF",
    fontSize: "1rem",
    fontWeight: "500",
    borderRadius: "0.75rem",
    textTransform: "none",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  secondaryButton: {
    background: "#FFFFFF",
    color: "#0DB14B",
    border: "solid 1px #0DB14B",
    padding: "0.5rem 1rem",
    fontSize: "1rem",
    fontWeight: "500",
    textTransform: "none",
    borderRadius: "0.75rem",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  buttonText: {
    [theme.breakpoints.up("md")]: {
      display: "block",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  mobileButtonText: {
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
  },
  loadingOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  circularProgress: {
    position: "relative",
    width: "60px",
    height: "60px",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      border: "4px solid rgba(13, 177, 75, 0.1)",
      borderTopColor: "#0DB14B",
      borderRadius: "50%",
      animation: "$spin 1s linear infinite",
    },
  },
  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}));

const StaticPropertyImage = () => {
  const router = useRouter();
  const classes = useStyles();
  const [showNumber, setShowNumber] = React.useState(false);
  const [openContactForm, setOpenContactForm] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation(); // Prevent the container click from firing
    if (window.innerWidth <= 960) {
      return true;
    }
    setShowNumber(true);
  };

  const handleOpenContactForm = (event) => {
    event.stopPropagation(); // Prevent the container click from firing
    setOpenContactForm(true);
  };

  const handleCloseContactForm = () => {
    setOpenContactForm(false);
  };

  const handleContainerClick = async (event) => {
    // Don't navigate if clicking on buttons
    if (event.target.closest("button") || event.target.closest(`.${classes.btnRow}`)) {
      return;
    }

    setIsLoading(true);
    try {
      await router.push("/about-us");
    } catch (error) {
      console.error("Navigation error:", error);
    }
  };

  return (
    <React.Fragment>
      <ContactFormModal
        open={openContactForm}
        onClose={handleCloseContactForm}
        source="Homepage"
      />
      <div className={classes.megaContainer}>
        <div
          className={classes.imageAndTextContainer}
          onClick={handleContainerClick}
          style={{ cursor: "pointer" }}
        >
          <img
            src="/images/icons/heavie/sentientindex.webp"
            alt={AppConfig.imageAltText}
            width={512}
            height={512}
            loading="eager"
            referrerPolicy="no-referrer"
            className={classes.illustrationSizer}
          />
          <div className={classes.overlayContainer}>
            <div className={classes.textContainer}>
              <Typography
                variant="h4"
                className={classes.heading}
              >
                Designing the foundations of tomorrow
              </Typography>
              <Typography
                variant="body2"
                className={classes.subheading}
              >
                Architecture shaped by intention, rooted in nature, built to stand the test of time.
              </Typography>
            </div>
            <div className={classes.btnRow}>
              {!showNumber ? (
                <Button
                  variant="outlined"
                  onClick={handleClick}
                  href={`tel:08071844962`}
                  title="View Number"
                  rel="noopener"
                  className={classes.primaryButton}
                  startIcon={<PhoneOutlined />}
                >
                  <span className={classes.buttonText}>View Number</span>
                  <span className={classes.mobileButtonText}>Call Now</span>
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  href={`tel:08071844962`}
                  title="Call Now"
                  rel="noopener"
                  className={classes.primaryButton}
                  startIcon={<PhoneOutlined />}
                  onClick={(e) => e.stopPropagation()} // Prevent container click
                >
                  08071844962
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={handleOpenContactForm}
                title="Get a callback"
                rel="noopener"
                className={classes.secondaryButton}
              >
                Get Callback
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isLoading && (
        <div className={classes.loadingOverlay}>
          <div className={classes.circularProgress} />
        </div>
      )}
    </React.Fragment>
  );
};

export default StaticPropertyImage;
